/**
 * CREATIVE DETONATOR - Manual Idea Entry Module
 * Allows users to add their own ideas without AI generation
 *
 * Use cases:
 * - Importing existing campaign concepts
 * - Adding client-provided ideas
 * - Capturing brainstorm notes
 * - Building a personal idea library
 *
 * @version 1.0
 */

import { api } from '../../../js/api/client.js';
import { Sounds } from '../../../js/shared/sounds.js';
import { Toast } from '../../../js/shared/toast.js';
import { Storage } from './storage.js';
import { createLogger } from '../../../js/core/logger.js';

const log = createLogger('manual-idea');

// ============================================================================
// CONSTANTS
// ============================================================================

const FORMATS = [
  { id: 'print', name: 'PRINT / OOH' },
  { id: 'social', name: 'SOCIAL MEDIA' },
  { id: 'video', name: 'VIDEO AD' },
  { id: 'digital', name: 'DIGITAL' },
  { id: 'experiential', name: 'EXPERIENTIAL' },
  { id: 'campaign', name: 'FULL CAMPAIGN' }
];

// ============================================================================
// MODULE STATE
// ============================================================================

const state = {
  isOpen: false,
  formData: {
    headline: '',
    visual: '',
    format: 'print',
    brandId: null,
    productId: null,
    triggerCode: null,
    benefit: '',
    notes: '',
    imageFile: null,
    imageUrl: null
  },
  allBrands: [],
  allTriggers: [],
  brandProducts: {} // Cache: brandId -> products[]
};

// Modal element reference
let modalElement = null;

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the Manual Idea module
 */
export async function init() {
  // Load brands
  await loadBrands();

  // Load triggers
  await loadTriggers();

  // Create modal element
  createModalElement();

  log.debug('Manual Idea module initialized');
}

/**
 * Load brands from API
 */
async function loadBrands() {
  try {
    const response = await api.getBrands();
    state.allBrands = response.brands || [];
  } catch (error) {
    log.warn('Failed to load brands:', error);
    state.allBrands = [];
  }
}

/**
 * Load triggers from DataStore or API
 */
async function loadTriggers() {
  try {
    if (window.DataStore?.triggers?.length > 0) {
      state.allTriggers = window.DataStore.triggers;
      return;
    }

    if (window.DataStore && typeof window.DataStore.init === 'function') {
      await window.DataStore.init();
      if (window.DataStore.triggers?.length > 0) {
        state.allTriggers = window.DataStore.triggers;
        return;
      }
    }

    // Fallback: fetch from API using shared client (cross-origin safe)
    const response = await api.getTriggers();
    state.allTriggers = response.triggers || [];
  } catch (error) {
    log.warn('Failed to load triggers:', error);
    state.allTriggers = [];
  }
}

/**
 * Load products for a specific brand
 */
async function loadProductsForBrand(brandId) {
  if (!brandId) return [];

  // Check cache first
  if (state.brandProducts[brandId]) {
    return state.brandProducts[brandId];
  }

  try {
    const response = await api.getProducts(brandId);
    const products = response.products || [];
    state.brandProducts[brandId] = products;
    log.debug(`Loaded ${products.length} products for brand ${brandId}`);
    return products;
  } catch (error) {
    log.warn('Failed to load products:', error);
    return [];
  }
}

/**
 * Populate product dropdown for a brand
 */
function populateProductDropdown(brandId) {
  const select = modalElement.querySelector('#mi-product');
  const products = state.brandProducts[brandId] || [];

  select.innerHTML = '<option value="">No specific product</option>' +
    products.map(p =>
      `<option value="${p.id}" data-benefit="${escapeHtml(p.default_benefit || '')}">${escapeHtml(p.name)}</option>`
    ).join('');
}

// ============================================================================
// MODAL CREATION
// ============================================================================

/**
 * Create the modal DOM element
 */
function createModalElement() {
  if (modalElement) return;

  modalElement = document.createElement('div');
  modalElement.id = 'manual-idea-modal';
  modalElement.className = 'modal-overlay';
  modalElement.innerHTML = getModalHTML();

  document.body.appendChild(modalElement);

  // Bind event listeners
  bindModalEvents();
}

/**
 * Get the modal HTML content
 */
function getModalHTML() {
  return `
    <div class="modal-container modal-md manual-idea-container">
      <div class="modal-titlebar">
        <div class="window-close modal-close" id="mi-close"></div>
        <div class="window-title">ADD IDEA MANUALLY</div>
      </div>

      <div class="modal-body manual-idea-body">
        <!-- Headline (required) -->
        <div class="mi-field">
          <label class="mi-label" for="mi-headline">HEADLINE *</label>
          <input type="text" id="mi-headline" class="form-input" placeholder="The main headline or tagline">
        </div>

        <!-- Visual Description -->
        <div class="mi-field">
          <label class="mi-label" for="mi-visual">VISUAL / EXECUTION</label>
          <textarea id="mi-visual" class="form-textarea" rows="3" placeholder="Describe the visual concept or execution..."></textarea>
        </div>

        <!-- Format & Brand Row -->
        <div class="mi-row">
          <div class="mi-field mi-field-half">
            <label class="mi-label" for="mi-format">FORMAT</label>
            <select id="mi-format" class="form-select">
              ${FORMATS.map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
            </select>
          </div>
          <div class="mi-field mi-field-half">
            <label class="mi-label" for="mi-brand">BRAND</label>
            <select id="mi-brand" class="form-select">
              <option value="">No brand</option>
            </select>
          </div>
        </div>

        <!-- Product (shown when brand is selected) -->
        <div class="mi-field" id="mi-product-field" style="display: none;">
          <label class="mi-label" for="mi-product">PRODUCT</label>
          <select id="mi-product" class="form-select">
            <option value="">No specific product</option>
          </select>
        </div>

        <!-- Benefit -->
        <div class="mi-field">
          <label class="mi-label" for="mi-benefit">BENEFIT / KEY MESSAGE</label>
          <input type="text" id="mi-benefit" class="form-input" placeholder="What's the main benefit or message?">
        </div>

        <!-- Trigger (optional) -->
        <div class="mi-field">
          <label class="mi-label" for="mi-trigger">TRIGGER (optional)</label>
          <select id="mi-trigger" class="form-select">
            <option value="">No trigger</option>
          </select>
        </div>

        <!-- Notes -->
        <div class="mi-field">
          <label class="mi-label" for="mi-notes">NOTES</label>
          <textarea id="mi-notes" class="form-textarea" rows="2" placeholder="Any additional notes..."></textarea>
        </div>

        <!-- Image Upload -->
        <div class="mi-field">
          <label class="mi-label">ATTACH IMAGE (optional)</label>
          <div class="mi-upload-zone" id="mi-upload-zone">
            <input type="file" id="mi-file-input" accept="image/*" hidden>
            <div class="mi-upload-placeholder" id="mi-upload-placeholder">
              <span class="mi-upload-icon">+</span>
              <span class="mi-upload-text">Drag & drop or click to upload</span>
            </div>
            <div class="mi-upload-preview" id="mi-upload-preview" style="display: none;">
              <img id="mi-preview-img" src="" alt="Preview">
              <button type="button" class="mi-remove-image" id="mi-remove-image">REMOVE</button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer manual-idea-footer">
        <button class="btn btn-outline" id="mi-cancel">CANCEL</button>
        <button class="btn btn-primary" id="mi-save">SAVE IDEA</button>
      </div>
    </div>
  `;
}

/**
 * Bind event listeners to modal elements
 */
function bindModalEvents() {
  if (!modalElement) return;

  // Close buttons
  modalElement.querySelector('#mi-close').addEventListener('click', close);
  modalElement.querySelector('#mi-cancel').addEventListener('click', close);

  // Close on overlay click
  modalElement.addEventListener('click', (e) => {
    if (e.target === modalElement) close();
  });

  // Form inputs
  modalElement.querySelector('#mi-headline').addEventListener('input', (e) => {
    state.formData.headline = e.target.value;
  });
  modalElement.querySelector('#mi-visual').addEventListener('input', (e) => {
    state.formData.visual = e.target.value;
  });
  modalElement.querySelector('#mi-format').addEventListener('change', (e) => {
    state.formData.format = e.target.value;
  });
  modalElement.querySelector('#mi-brand').addEventListener('change', async (e) => {
    const brandId = e.target.value || null;
    state.formData.brandId = brandId;
    state.formData.productId = null; // Reset product when brand changes

    // Show/hide product field based on brand selection
    const productField = modalElement.querySelector('#mi-product-field');
    const productSelect = modalElement.querySelector('#mi-product');

    if (brandId) {
      productField.style.display = '';
      // Load products for this brand
      await loadProductsForBrand(brandId);
      populateProductDropdown(brandId);
    } else {
      productField.style.display = 'none';
      productSelect.innerHTML = '<option value="">No specific product</option>';
    }
  });
  modalElement.querySelector('#mi-product').addEventListener('change', (e) => {
    state.formData.productId = e.target.value || null;
    // If product has a default benefit, use it
    const selectedOption = e.target.selectedOptions[0];
    const defaultBenefit = selectedOption?.dataset.benefit;
    if (defaultBenefit && !state.formData.benefit) {
      state.formData.benefit = defaultBenefit;
      modalElement.querySelector('#mi-benefit').value = defaultBenefit;
    }
  });
  modalElement.querySelector('#mi-benefit').addEventListener('input', (e) => {
    state.formData.benefit = e.target.value;
  });
  modalElement.querySelector('#mi-trigger').addEventListener('change', (e) => {
    state.formData.triggerCode = e.target.value || null;
  });
  modalElement.querySelector('#mi-notes').addEventListener('input', (e) => {
    state.formData.notes = e.target.value;
  });

  // Image upload
  const uploadZone = modalElement.querySelector('#mi-upload-zone');
  const fileInput = modalElement.querySelector('#mi-file-input');

  uploadZone.addEventListener('click', () => fileInput.click());
  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.classList.add('dragover');
  });
  uploadZone.addEventListener('dragleave', () => {
    uploadZone.classList.remove('dragover');
  });
  uploadZone.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadZone.classList.remove('dragover');
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  });
  fileInput.addEventListener('change', (e) => {
    if (e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  });

  // Remove image
  modalElement.querySelector('#mi-remove-image').addEventListener('click', (e) => {
    e.stopPropagation();
    removeImage();
  });

  // Save button
  modalElement.querySelector('#mi-save').addEventListener('click', save);

  // ESC to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.isOpen) {
      close();
    }
  });
}

// ============================================================================
// IMAGE HANDLING
// ============================================================================

/**
 * Handle file selection
 */
function handleFileSelect(file) {
  if (!file.type.startsWith('image/')) {
    Toast.show('Please select an image file', 'error');
    return;
  }

  state.formData.imageFile = file;

  // Show preview
  const reader = new FileReader();
  reader.onload = (e) => {
    const previewImg = modalElement.querySelector('#mi-preview-img');
    previewImg.src = e.target.result;
    modalElement.querySelector('#mi-upload-placeholder').style.display = 'none';
    modalElement.querySelector('#mi-upload-preview').style.display = 'flex';
  };
  reader.readAsDataURL(file);

  Sounds.play('click');
}

/**
 * Remove selected image
 */
function removeImage() {
  state.formData.imageFile = null;
  state.formData.imageUrl = null;

  const fileInput = modalElement.querySelector('#mi-file-input');
  fileInput.value = '';

  modalElement.querySelector('#mi-upload-placeholder').style.display = 'flex';
  modalElement.querySelector('#mi-upload-preview').style.display = 'none';

  Sounds.play('click');
}

// ============================================================================
// MODAL CONTROL
// ============================================================================

/**
 * Open the Manual Idea modal
 */
export async function open() {
  if (!modalElement) {
    await init();
  }

  // Reset form
  resetForm();

  // Populate brand dropdown
  populateBrandDropdown();

  // Populate trigger dropdown
  populateTriggerDropdown();

  // Pre-select loaded brand if any
  const loadedBrandId = Storage.get('loaded_brand');
  if (loadedBrandId) {
    state.formData.brandId = loadedBrandId;
    const brandSelect = modalElement.querySelector('#mi-brand');
    if (brandSelect) brandSelect.value = loadedBrandId;

    // Show product field and load products for pre-selected brand
    const productField = modalElement.querySelector('#mi-product-field');
    productField.style.display = '';
    await loadProductsForBrand(loadedBrandId);
    populateProductDropdown(loadedBrandId);
  }

  // Show modal
  modalElement.classList.add('active');
  document.body.style.overflow = 'hidden';
  state.isOpen = true;

  Sounds.play('click');
  log.debug('Manual Idea modal opened');

  // Focus headline input
  setTimeout(() => {
    modalElement.querySelector('#mi-headline').focus();
  }, 100);
}

/**
 * Close the Manual Idea modal
 */
export function close() {
  if (!modalElement) return;

  modalElement.classList.remove('active');
  document.body.style.overflow = '';
  state.isOpen = false;

  Sounds.play('click');
  log.debug('Manual Idea modal closed');
}

/**
 * Reset form to default state
 */
function resetForm() {
  state.formData = {
    headline: '',
    visual: '',
    format: 'print',
    brandId: null,
    productId: null,
    triggerCode: null,
    benefit: '',
    notes: '',
    imageFile: null,
    imageUrl: null
  };

  if (modalElement) {
    modalElement.querySelector('#mi-headline').value = '';
    modalElement.querySelector('#mi-visual').value = '';
    modalElement.querySelector('#mi-format').value = 'print';
    modalElement.querySelector('#mi-brand').value = '';
    modalElement.querySelector('#mi-product').value = '';
    modalElement.querySelector('#mi-product-field').style.display = 'none';
    modalElement.querySelector('#mi-benefit').value = '';
    modalElement.querySelector('#mi-trigger').value = '';
    modalElement.querySelector('#mi-notes').value = '';
    modalElement.querySelector('#mi-file-input').value = '';
    modalElement.querySelector('#mi-upload-placeholder').style.display = 'flex';
    modalElement.querySelector('#mi-upload-preview').style.display = 'none';
  }
}

/**
 * Populate brand dropdown
 */
function populateBrandDropdown() {
  const select = modalElement.querySelector('#mi-brand');
  select.innerHTML = '<option value="">No brand</option>' +
    state.allBrands.map(b => `<option value="${b.id}">${escapeHtml(b.name)}</option>`).join('');
}

/**
 * Populate trigger dropdown
 */
function populateTriggerDropdown() {
  const select = modalElement.querySelector('#mi-trigger');
  select.innerHTML = '<option value="">No trigger</option>' +
    state.allTriggers.map(t => `<option value="${t.code}">${t.code}: ${escapeHtml(t.name)}</option>`).join('');
}

// ============================================================================
// VALIDATION & SAVE
// ============================================================================

/**
 * Validate form
 */
function validate() {
  if (!state.formData.headline.trim()) {
    Toast.show('Headline is required', 'error');
    modalElement.querySelector('#mi-headline').focus();
    return false;
  }
  return true;
}

/**
 * Save the manual idea
 */
async function save() {
  if (!validate()) return;

  const saveBtn = modalElement.querySelector('#mi-save');
  saveBtn.disabled = true;
  saveBtn.textContent = 'SAVING...';

  try {
    // Upload image if provided
    let imageUrl = state.formData.imageUrl;
    if (state.formData.imageFile) {
      imageUrl = await uploadImage(state.formData.imageFile);
    }

    // Save idea via API
    const result = await api.createManualIdea({
      headline: state.formData.headline.trim(),
      visual: state.formData.visual.trim(),
      format: state.formData.format,
      brandId: state.formData.brandId,
      productId: state.formData.productId,
      triggerCode: state.formData.triggerCode,
      benefit: state.formData.benefit.trim(),
      notes: state.formData.notes.trim(),
      imageUrl: imageUrl
    });

    if (result.success) {
      Toast.show('Idea saved!', 'success');
      Sounds.play('success');
      close();

      // Refresh ideas list if on ideas page
      if (window.IdeasModule?.loadAndRender) {
        await window.IdeasModule.loadAndRender();
      }

      // Dispatch event for other modules to react
      window.dispatchEvent(new CustomEvent('manual-idea:saved', {
        detail: { idea: result.idea }
      }));
    } else {
      throw new Error(result.error || 'Failed to save idea');
    }

  } catch (err) {
    log.error('Failed to save idea:', err);
    Toast.show('Failed to save idea', 'error');
  } finally {
    saveBtn.disabled = false;
    saveBtn.textContent = 'SAVE IDEA';
  }
}

/**
 * Upload image to server
 */
async function uploadImage(file) {
  const formData = new FormData();
  formData.append('image', file);

  const response = await api.uploadManualImage(formData);
  return response.url;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Escape HTML for safe rendering
 */
function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ============================================================================
// EXPORTS
// ============================================================================

export const ManualIdea = {
  init,
  open,
  close,
  state
};

// Make available globally
window.ManualIdea = ManualIdea;
